import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import {
  getAvailableTournaments,
  getAvailableTournamentsByFilter,
  reset,
  selectAvailableTournamentsOnPage,
  selectAvailableTournamentsPagination,
  selectFilter,
  selectLoading,
  selectUserId,
  setFilter,
} from 'modules/tournaments/store/available-tournaments';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useQueryParams } from 'shared/hooks/use-query-params';
import { AppDispatch } from 'store/store';

export type IInitValueUseAvailableTournamentsProps = {
  page?: number;
};

export function useAvailableTournaments(options?: IInitValueUseAvailableTournamentsProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isChecking } = useAuth();

  const { page: initPage = 1 } = options || {};

  const [params, setParams] = useQueryParams({
    page: {
      key: 'page',
      defaultValue: initPage,
      parser: Number,
    },
  });

  const filter = useSelector(selectFilter);
  const availableTournamentsOnPage = useSelector(selectAvailableTournamentsOnPage(params.page, 6));
  const { pages, allDataLoaded, limit, allItemsCount } = useSelector(selectAvailableTournamentsPagination(6));
  const loadingState = useSelector(selectLoading);
  const userId = useSelector(selectUserId);

  const changeFilter = useCallback(
    (key: keyof typeof params | 'filter', value: any) => {
      switch (key) {
        case 'page':
          setParams({ page: value });
          if (value === pages && !allDataLoaded) {
            dispatch(getAvailableTournaments({ offset: allItemsCount, limit: limit }));
          }
          break;
        case 'filter':
          dispatch(setFilter({ text: value }));
          dispatch(getAvailableTournamentsByFilter({ offset: 0, limit: limit, namePart: value }));
          setParams({ page: 1 });
          break;
        default:
          toast.warn('Неизвестный фильтр');
      }
    },
    [allDataLoaded, allItemsCount, dispatch, limit, pages, setParams],
  );

  useEffect(() => {
    if (!user?.id) {
      console.warn('User ID is not defined');
      return;
    }

    if (!userId) {
      return;
    }

    if (userId !== user.id) {
      dispatch(reset({ userId: user.id }));
      dispatch(getAvailableTournaments({ limit: limit }));
    }
  }, [dispatch, limit, user?.id, userId]);

  useEffect(() => {
    if (!loadingState.isLoading && !loadingState.isLoaded && user?.id) {
      dispatch(getAvailableTournaments({ limit: limit }));
    }
  }, [dispatch, loadingState.isLoaded, loadingState.isLoading, limit, user]);

  return {
    data: availableTournamentsOnPage,
    currentPage: params.page,
    pages: pages,
    changeFilter,
    filter: filter.text,
    isLoading: loadingState.isLoading || isChecking,
  };
}
